import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';

/**
 * Hook that checks whether the current user has overwatch (cheat-detection)
 * access. Used to conditionally render the hidden navigation entry and guard
 * overwatch routes. Never exposes the existence of reports to users without
 * access — the /status endpoint only returns booleans.
 */
export default function useOverwatchAccess() {
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setHasAccess(false);
      setCanManage(false);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/overwatch/status`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        setHasAccess(false);
        setCanManage(false);
      } else {
        const data = await res.json();
        setHasAccess(!!data.has_access);
        setCanManage(!!data.can_manage);
      }
    } catch {
      setHasAccess(false);
      setCanManage(false);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, getAuthHeaders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { hasAccess, canManage, loading, refresh };
}
